import { isCurve25519, requireSigningCurve, getWebCryptoSubtle } from "./CurveUtil";
import {
  loadEccSigningPrivateKey,
  loadEccSigningPublicKey,
} from "./EccEncryption";
import {
  convertArrayBufferToBase64String,
  convertBase64StringToArrayBuffer,
} from "../util/Utility";
import {
  ECDSA_COMPONENT_LENGTH,
  p1363ToDer,
  toP1363,
} from "./EcdsaSignatureFormat";

const nodeCrypto = typeof require !== "undefined" ? require("crypto") : null;

/**
 * True only under a real Node runtime. Bundlers substitute `crypto-browserify` for `crypto`
 * in browser builds, so the presence of the module alone is not a usable signal.
 */
const isNodeRuntime =
  typeof process !== "undefined" && !!process.versions && !!process.versions.node;

function useNodeCrypto(methodName) {
  return isNodeRuntime && nodeCrypto && typeof nodeCrypto[methodName] === "function";
}

function ecdsaComponentLength(resolvedCurve) {
  const length = ECDSA_COMPONENT_LENGTH[resolvedCurve];
  if (!length) {
    throw new Error(`Unsupported ECDSA signing curve: ${resolvedCurve}`);
  }
  return length;
}

/**
 * Signs the plaintext with the given ECC/EdDSA private key.
 * For P-384: ECDSA with SHA-384, emitted as ASN.1 DER to match LMS `SHA384withECDSA`.
 * For Curve25519: Ed25519.
 *
 * @param {String} privateKeyString Private key in PEM format.
 * @param {String} plainTextString The plaintext to be signed.
 * @param {String} signingCurve The signing curve from storage/API.
 * @returns {String} The signature as a base64 string.
 */
export const signWithEcc = async (privateKeyString, plainTextString, signingCurve) => {
  const resolvedCurve = requireSigningCurve(signingCurve);

  if (isCurve25519(resolvedCurve)) {
    if (useNodeCrypto("sign")) {
      const signature = nodeCrypto.sign(
        null,
        Buffer.from(plainTextString),
        privateKeyString,
      );
      return signature.toString("base64");
    }
    const privateKey = await loadEccSigningPrivateKey(
      privateKeyString,
      resolvedCurve,
    );
    const signature = await getWebCryptoSubtle().sign(
      { name: "Ed25519" },
      privateKey,
      new TextEncoder().encode(plainTextString),
    );
    return convertArrayBufferToBase64String(signature);
  }

  if (useNodeCrypto("createSign")) {
    const sign = nodeCrypto.createSign("sha384");
    sign.update(plainTextString);
    sign.end();
    return sign.sign(privateKeyString, "base64");
  }

  const privateKey = await loadEccSigningPrivateKey(
    privateKeyString,
    resolvedCurve,
  );
  const p1363Signature = await getWebCryptoSubtle().sign(
    { name: "ECDSA", hash: "SHA-384" },
    privateKey,
    new TextEncoder().encode(plainTextString),
  );
  return convertArrayBufferToBase64String(p1363ToDer(new Uint8Array(p1363Signature)));
};

/**
 * Verifies the given signature.
 * For P-384: ECDSA with SHA-384; DER input is converted for Web Crypto.
 * For Curve25519: Ed25519.
 *
 * @param {String} plainTextString The plaintext as a string.
 * @param {String} signatureString The signature to be verified (base64).
 * @param {String} publicKeyString The public key in PEM format.
 * @param {String} signingCurve The signing curve from storage/API.
 * @returns {Boolean} Whether the verification succeeded.
 */
export const verifySignatureWithEcc = async (
  plainTextString,
  signatureString,
  publicKeyString,
  signingCurve,
) => {
  const resolvedCurve = requireSigningCurve(signingCurve);

  if (isCurve25519(resolvedCurve)) {
    if (useNodeCrypto("verify")) {
      return nodeCrypto.verify(
        null,
        Buffer.from(plainTextString),
        publicKeyString,
        Buffer.from(signatureString, "base64"),
      );
    }
    const publicKey = await loadEccSigningPublicKey(
      publicKeyString,
      resolvedCurve,
    );
    return getWebCryptoSubtle().verify(
      { name: "Ed25519" },
      publicKey,
      convertBase64StringToArrayBuffer(signatureString),
      new TextEncoder().encode(plainTextString),
    );
  }

  if (useNodeCrypto("createVerify")) {
    const verify = nodeCrypto.createVerify("sha384");
    verify.update(plainTextString);
    verify.end();
    return verify.verify(publicKeyString, signatureString, "base64");
  }

  const publicKey = await loadEccSigningPublicKey(
    publicKeyString,
    resolvedCurve,
  );
  let p1363Signature;
  try {
    p1363Signature = toP1363(
      new Uint8Array(convertBase64StringToArrayBuffer(signatureString)),
      ecdsaComponentLength(resolvedCurve),
    );
  } catch (error) {
    return false;
  }
  return getWebCryptoSubtle().verify(
    { name: "ECDSA", hash: "SHA-384" },
    publicKey,
    p1363Signature,
    new TextEncoder().encode(plainTextString),
  );
};
